import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import { useData } from "../lib/DataContext";
import { useAuth } from "../lib/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate } from "../lib/ui";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const SEV_COLOR = { Low: "#16A34A", Medium: "#2563EB", High: "#EA580C", Critical: "#DC2626" };

export default function Registers() {
  return (
    <div className="p-6">
      <PageHeader title="Risk & Issue Registers" subtitle="A risk may happen; an issue already has — track both centrally" />
      <Tabs defaultValue="risks">
        <TabsList className="mb-4">
          <TabsTrigger value="risks" data-testid="reg-tab-risks">Risk Register</TabsTrigger>
          <TabsTrigger value="issues" data-testid="reg-tab-issues">Issue Register</TabsTrigger>
        </TabsList>
        <TabsContent value="risks"><Risks /></TabsContent>
        <TabsContent value="issues"><Issues /></TabsContent>
      </Tabs>
    </div>
  );
}

function Risks() {
  const { users, projects, departments } = useData();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const canCreate = ["master_admin", "admin", "manager"].includes(user?.role);
  const load = () => api.get("/risks").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      {canCreate && <div className="flex justify-end mb-3"><Button onClick={() => setShow(true)} data-testid="new-risk-button"><Plus size={16} className="mr-1" /> New Risk</Button></div>}
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5">ID</th><th className="px-4 py-2.5">Risk</th><th className="px-4 py-2.5">Probability</th><th className="px-4 py-2.5">Impact</th><th className="px-4 py-2.5">Score</th><th className="px-4 py-2.5">Severity</th><th className="px-4 py-2.5">Target</th><th className="px-4 py-2.5">Status</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`risk-${r.human_id}`}>
                <td className="px-4 py-2 font-mono text-xs text-slate-400">{r.human_id}</td>
                <td className="px-4 py-2 font-medium">{r.title}</td>
                <td className="px-4 py-2">{r.probability}</td><td className="px-4 py-2">{r.impact}</td>
                <td className="px-4 py-2 font-mono">{r.risk_score}</td>
                <td className="px-4 py-2"><span className="text-xs rounded-full px-2 py-0.5" style={{ backgroundColor: (SEV_COLOR[r.severity]||"#64748B")+"20", color: SEV_COLOR[r.severity]||"#64748B" }}>{r.severity}</span></td>
                <td className="px-4 py-2">{fmtDate(r.target_date)}</td><td className="px-4 py-2">{r.status}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">No risks logged.</td></tr>}
          </tbody>
        </table>
      </div>
      {show && <RiskModal users={users} projects={projects} departments={departments} onClose={() => setShow(false)} onDone={() => { setShow(false); load(); }} />}
    </div>
  );
}

function RiskModal({ users, projects, departments, onClose, onDone }) {
  const [f, setF] = useState({ title: "", category: "", probability: "Medium", impact: "Medium", owner_id: "", mitigation_plan: "", target_date: "", project_id: "", department_id: "", status: "Open" });
  const submit = async () => {
    if (!f.title) return toast.error("Risk title required");
    const p = { ...f }; Object.keys(p).forEach((k) => p[k] === "" && (p[k] = null)); p.title = f.title;
    await api.post("/risks", p); toast.success("Risk added"); onDone();
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent data-testid="risk-modal">
      <DialogHeader><DialogTitle>New Risk</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Risk *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Probability</Label><Sel v={f.probability} set={(v) => setF({ ...f, probability: v })} opts={["Low","Medium","High"]} /></div>
          <div><Label>Impact</Label><Sel v={f.impact} set={(v) => setF({ ...f, impact: v })} opts={["Low","Medium","High","Critical"]} /></div>
        </div>
        <div><Label>Owner</Label><Sel v={f.owner_id} set={(v) => setF({ ...f, owner_id: v })} opts={users.map((u) => [u.id, u.full_name])} ph="Select" /></div>
        <div><Label>Mitigation Plan</Label><Textarea value={f.mitigation_plan} onChange={(e) => setF({ ...f, mitigation_plan: e.target.value })} /></div>
        <div><Label>Target Date</Label><Input type="date" value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="risk-submit">Save</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function Issues() {
  const { users, projects, departments } = useData();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const canCreate = ["master_admin", "admin", "manager", "employee"].includes(user?.role);
  const load = () => api.get("/issues").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      {canCreate && <div className="flex justify-end mb-3"><Button onClick={() => setShow(true)} data-testid="new-issue-button"><Plus size={16} className="mr-1" /> New Issue</Button></div>}
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5">ID</th><th className="px-4 py-2.5">Issue</th><th className="px-4 py-2.5">Severity</th><th className="px-4 py-2.5">Impact</th><th className="px-4 py-2.5">Due</th><th className="px-4 py-2.5">Status</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`issue-${r.human_id}`}>
                <td className="px-4 py-2 font-mono text-xs text-slate-400">{r.human_id}</td>
                <td className="px-4 py-2 font-medium">{r.title}</td>
                <td className="px-4 py-2"><span className="text-xs rounded-full px-2 py-0.5" style={{ backgroundColor: (SEV_COLOR[r.severity]||"#64748B")+"20", color: SEV_COLOR[r.severity]||"#64748B" }}>{r.severity}</span></td>
                <td className="px-4 py-2">{r.impact}</td><td className="px-4 py-2">{fmtDate(r.due_date)}</td><td className="px-4 py-2">{r.status}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No issues logged.</td></tr>}
          </tbody>
        </table>
      </div>
      {show && <IssueModal users={users} onClose={() => setShow(false)} onDone={() => { setShow(false); load(); }} />}
    </div>
  );
}

function IssueModal({ users, onClose, onDone }) {
  const [f, setF] = useState({ title: "", description: "", severity: "Medium", impact: "", owner_id: "", resolution_plan: "", due_date: "", status: "Open", root_cause: "" });
  const submit = async () => {
    if (!f.title) return toast.error("Issue title required");
    const p = { ...f }; Object.keys(p).forEach((k) => p[k] === "" && (p[k] = null)); p.title = f.title;
    await api.post("/issues", p); toast.success("Issue logged"); onDone();
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent data-testid="issue-modal">
      <DialogHeader><DialogTitle>New Issue</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Issue *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
        <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Severity</Label><Sel v={f.severity} set={(v) => setF({ ...f, severity: v })} opts={["Low","Medium","High","Critical"]} /></div>
          <div><Label>Owner</Label><Sel v={f.owner_id} set={(v) => setF({ ...f, owner_id: v })} opts={users.map((u) => [u.id, u.full_name])} ph="Select" /></div>
        </div>
        <div><Label>Resolution Plan</Label><Textarea value={f.resolution_plan} onChange={(e) => setF({ ...f, resolution_plan: e.target.value })} /></div>
        <div><Label>Due Date</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="issue-submit">Save</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function Sel({ v, set, opts, ph }) {
  const norm = opts.map((o) => (Array.isArray(o) ? o : [o, o]));
  return (
    <Select value={v} onValueChange={set}>
      <SelectTrigger><SelectValue placeholder={ph || ""} /></SelectTrigger>
      <SelectContent>{norm.map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}</SelectContent>
    </Select>
  );
}
